import React from "react";
import { Navigate } from "react-router-dom-v5-compat";
import { ErrorBoundary } from "../ErrorBoundary";

type RouteValidation<R> = () => R;
type DeriveViewProps<R, V> = (routeProps: R) => V;

type ContainerType<R, V> = {
  useDeriveViewProps: DeriveViewProps<R, V>;
  Component: React.ComponentType<V>;
};
type ContainerComponentProps<R, V> = {
  routeProps: R;
} & ContainerType<R, V>;

const ContainerComponent = <R, V extends object>({
  routeProps,
  Component,
  useDeriveViewProps,
}: ContainerComponentProps<R, V>) => {
  const viewProps = useDeriveViewProps(routeProps);
  return <Component {...viewProps} />;
};

interface PageRouteContainerProps<R, V> {
  route: {
    useValidateRouteProps: RouteValidation<R>;
    errorRedirectPath: string;
  };
  container: {
    useDeriveViewProps: DeriveViewProps<R, V>;
    Component: React.ComponentType<V>;
  };
}

export const PageRouteContainer = <R, V extends object>({
  route,
  container,
}: React.PropsWithChildren<PageRouteContainerProps<R, V>>) => {
  try {
    const routeProps = route.useValidateRouteProps();

    const { useDeriveViewProps, Component } = container;
    return (
      <ContainerComponent
        routeProps={routeProps}
        Component={Component}
        useDeriveViewProps={useDeriveViewProps}
      />
    );
  } catch (e) {
    console.error("Error validating route props:", e);
    return <Navigate to={route.errorRedirectPath} replace />;
  }
};
